import {
	dispatchAcknowledgedEvent,
	openResourceSection,
} from '../../core/resourceEvents';
import {highlightExistingAction} from '../../core/actionHighlighter';
import {
	AUDIT_LOGS_FILTER_EVENT,
	AUDIT_LOGS_FOCUS_EVENT,
	AUDIT_LOGS_SECTION_EVENT,
	SNAPSHOT_HISTORY_SECTION_EVENT,
	SNAPSHOT_HISTORY_FILTER_EVENT,
	SYSTEM_LOGS_SECTION_EVENT,
} from './events';
import {normalizeAuditLogSearchQuery} from './listApply';

const AUDIT_SECTIONS = new Set(['maintenance.audit_logs']);
const SNAPSHOT_SECTIONS = new Set(['maintenance.snapshot_history']);
const SYSTEM_SECTIONS = new Set(['maintenance.system_logs']);

/**
 * Open Audit Logs and optionally click Filter or Download.
 * @param {object} command experience command
 * @return {Promise<void>} completion
 */
export async function openAuditLogsSection(command = {}) {
	const seen = await openResourceSection(
		AUDIT_LOGS_SECTION_EVENT,
		'maintenance.audit_logs',
		AUDIT_SECTIONS,
	);
	const target = command.params?.action_target;
	const activation = {
		activate: command.params?.action_activation === 'auto',
		commandId: command.command_id,
	};
	if (target === 'download' &&
		!highlightExistingAction(['Download'], [], activation)) {
		throw new Error('audit_logs_download_unavailable');
	}
	if (target === 'filter' &&
		!highlightExistingAction(['Filter'], [], activation)) {
		throw new Error('audit_logs_filter_unavailable');
	}
	// Carried back rather than awaited and dropped. The section says
	// what the page actually opened; a wrapper returning nothing
	// turns that into an empty acknowledgement, and the backend then
	// cannot tell a section that opened from one that did not.
	return seen;
}

/**
 * Open Snapshot History on the Logs tab.
 * @param {object} command experience command
 * @return {Promise<void>} completion
 */
export async function openSnapshotHistorySection(command = {}) {
	const seen = await openResourceSection(
		SNAPSHOT_HISTORY_SECTION_EVENT,
		'maintenance.snapshot_history',
		SNAPSHOT_SECTIONS,
	);
	const target = command.params?.action_target;
	const activation = {
		activate: command.params?.action_activation === 'auto',
		commandId: command.command_id,
	};
	if (target === 'download' &&
		!highlightExistingAction(['Download'], [], activation)) {
		throw new Error('snapshot_history_download_unavailable');
	}
	// Carried back rather than awaited and dropped. The section says
	// what the page actually opened; a wrapper returning nothing
	// turns that into an empty acknowledgement, and the backend then
	// cannot tell a section that opened from one that did not.
	return seen;
}

/**
 * Open System Logs on the Logs tab.
 * @return {Promise<void>} completion
 */
export function openSystemLogsSection() {
	return openResourceSection(
		SYSTEM_LOGS_SECTION_EVENT,
		'maintenance.system_logs',
		SYSTEM_SECTIONS,
	);
}

/**
 * Apply an Audit Logs search or filter.
 * Retries until Audit Logs is mounted, so a search from another page still
 * lands in the table search box.
 * @param {object} command filter command
 * @return {Promise<object>} completion payload
 */
export function applyAuditLogsFilter(command) {
	const params = command.params || {};
	const query = normalizeAuditLogSearchQuery(params.query);
	const filters = {};
	Object.entries(params).forEach(([key, value]) => {
		if (key === 'query' || key === 'resource') return;
		const text = String(value ?? '').trim();
		if (text) filters[key] = text;
	});
	const payload = !query && !Object.keys(filters).length
		? {query: '', clear: true, resource: 'audit_log'}
		: {query, ...filters, resource: 'audit_log'};
	return new Promise((resolve, reject) => {
		let settled = false;
		const finish = (callback) => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			clearInterval(retry);
			callback();
		};
		const detail = {
			...payload,
			complete: (result) => finish(() => resolve(result && typeof result === 'object'
				? result
				: {})),
			fail: () => finish(() => reject(new Error('audit_logs_filter_failed'))),
		};
		const send = () => {
			if (!settled) {
				window.dispatchEvent(new CustomEvent(
					AUDIT_LOGS_FILTER_EVENT,
					{detail},
				));
			}
		};
		const timer = setTimeout(
			() => finish(() => reject(new Error('audit_logs_filter_timeout'))),
			5000,
		);
		const retry = setInterval(send, 150);
		send();
	});
}

/**
 * Apply Snapshot History search.
 * Retries until Snapshot History is mounted, so a search from another page
 * still lands in the table search box.
 * @param {object} command filter command
 * @return {Promise<object>} completion payload
 */
export function applySnapshotHistoryFilter(command) {
	const params = command.params || {};
	const query = String(params.query || '').trim();
	const payload = !query
		? {query: '', clear: true, resource: 'ap_snapshot'}
		: {query, resource: 'ap_snapshot'};
	return new Promise((resolve, reject) => {
		let settled = false;
		const finish = (callback) => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			clearInterval(retry);
			callback();
		};
		const detail = {
			...payload,
			complete: (result) => finish(() => resolve(result && typeof result === 'object'
				? result
				: {})),
			fail: () => finish(() => reject(new Error('snapshot_search_failed'))),
		};
		const send = () => {
			if (!settled) {
				window.dispatchEvent(new CustomEvent(
					SNAPSHOT_HISTORY_FILTER_EVENT,
					{detail},
				));
			}
		};
		const timer = setTimeout(
			() => finish(() => reject(new Error('snapshot_search_timeout'))),
			5000,
		);
		const retry = setInterval(send, 150);
		send();
	});
}

/**
 * Focus a resolved Audit Logs row.
 * @param {object} command focus command
 * @return {Promise<void>} completion acknowledgement
 */
export function focusAuditLog(command) {
	const entityId = command.params?.entity_id;
	if (typeof entityId !== 'string' || !entityId) {
		return Promise.reject(new Error('invalid_audit_log_entity'));
	}
	return dispatchAcknowledgedEvent(AUDIT_LOGS_FOCUS_EVENT, {
		entityId,
		action_target: command.params?.action_target,
		action_activation: command.params?.action_activation,
		resource: 'audit_log',
	});
}
